import { ThemedSelect } from '@components/common/ThemedSelect';
import { useAuthStore } from '@stores/authStore';
import { useTeams } from '../../hooks/useTeams';
import {
  orderTeamsForPicker,
  teamOptionLabel,
  teamsInViewerScope,
} from '../settings/TeamsSettings/teamHierarchy';

interface TeamFilterPickerProps {
  /** '' or undefined = all teams. */
  value?: string;
  onChange: (teamId: string | undefined) => void;
  className?: string;
  /** Visible label; also the accessible name. */
  label?: string;
}

/**
 * "Team" picker for the dashboard and Reports. A parent team covers its
 * sub-teams. Admins see every team; supervisors only the teams they belong to
 * or lead (the server refuses anything else). Renders nothing for agents —
 * their figures are always their own — or when there are no teams to offer.
 */
export function TeamFilterPicker({ value, onChange, className, label = 'Team' }: TeamFilterPickerProps) {
  const user = useAuthStore((s) => s.user);
  const role = (user?.role || '').toLowerCase();
  const isAdmin = role === 'admin' || role === 'owner';
  const canFilter = isAdmin || role === 'supervisor';
  const { data } = useTeams({ isActive: true, limit: 200 }, { enabled: canFilter });

  if (!canFilter) return null;
  const options = orderTeamsForPicker(
    teamsInViewerScope(data?.data ?? [], { userId: user?.id, isAdmin }),
  );
  if (options.length === 0) return null;

  return (
    <ThemedSelect
      aria-label={label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      className={
        className ??
        'h-[34px] rounded-[10px] border border-[var(--co-line)] bg-[var(--co-sunken)] px-3 text-sm text-[var(--co-ink)]'
      }
    >
      <option value="">{isAdmin ? 'All teams' : 'My teams'}</option>
      {options.map((team) => (
        <option key={team.id} value={team.id}>
          {teamOptionLabel(team)}
        </option>
      ))}
    </ThemedSelect>
  );
}
